import React, { useState, useEffect } from "react";
import axios from "axios";
import { Search } from "lucide-react";
import { FaArrowLeft, FaEdit, FaMoneyCheckAlt, FaPrint } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const PaymentView = () => {
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [freesearch, setFreesearch] = useState("-");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const paymentsPerPage = 50;
  const [selectedPV, setSelectedPV] = useState(null);
  const COMPANY_CODE = localStorage.getItem("companyCode") || "-";

  // Consolidated to fire cleanly whenever page or search keyword updates
  useEffect(() => {
    fetchPayments(currentPage, freesearch);
  }, [currentPage, freesearch]);

  const fetchPayments = async (page = 1, search = "-") => {
    setIsLoading(true);
    setError(null);
    try {
      const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
      const currentDepartment =
        localStorage.getItem("currentSelectedDepartment") || "MAIN";

      // Dynamically toggle payload sizing bounds based on search engagement
      const isSearching = search !== "-" && search.trim() !== "";
      const limit = isSearching ? 1000 : 100;

      const apiUrl = `${apiPaths[0]?.API_CONFIG}/api/CRMDocListView/${COMPANY_CODE}/PAYMENT/-/-/-/${encodeURIComponent(search)}/-/${currentDepartment}/${page}/${limit}`;

      const response = await axios.get(apiUrl);
      setPayments(response.data || []);
      console.log(`Fetched payments for page ${page} with limit ${limit}:`, response.data);
    } catch (err) {
      console.error("Error while getting printing payment:", err);
      setError("Failed to fetch payments. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    // Fall back to server wildcard flag '-' if search bar is cleared
    if (term.trim() === "") {
      setFreesearch("-");
    } else {
      setFreesearch(term);
    }
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(payments.length / paymentsPerPage));

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const navigate = useNavigate();
  const onBack = () => {
    navigate("/payment");
  };

  const onEdit = (rec) => {
    console.log(" ~ onEdit ~ rec:", rec);

    const modifyVoucherData = {
      modifyVNO: rec.PV_NO?.trim(),
      modifyVType: rec.JVTYPE,
      modifyVDate: dayjs(rec.RV_DATE).format("YYYY-MM-DD"),
      amount: rec.AMOUNT,
      description: rec.COMMENTS,
    };

    navigate("/voucher", { state: { modifyVoucherData } });
  };

  const onPrint = (data) => {
    console.log("Print PV :", data);
    setSelectedPV(data);
    navigate(`/paymentPreview`, { state: { pvPrintData: data } });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center gap-6">
        <h2 className="text-xl text-gray-800 mb-4">Payment View</h2>

        <div className="relative mb-4 w-[40%]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by any field..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            aria-label="Search payments"
          />
        </div>
        <button
          onClick={onBack}
          className="mb-3 relative bg-gray-600 text-white font-medium text-[17px] px-2 py-[0.25em] pl-3 h-[1.8em] rounded-[0.9em] flex items-center overflow-hidden cursor-pointer group"
        >
          <span className="mr-10">Back</span>
          <div className="absolute right-[0.3em] bg-white h-[1.4em] w-[1.4em] rounded-[0.7em] flex items-center justify-center transition-all duration-300 group-hover:w-[calc(100%-0.6em)] active:scale-95">
            <FaArrowLeft className="text-gray-600" />
          </div>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8 my-8 inset-0">
          <div className="w-10 h-10 border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto bg-white rounded-lg shadow max-h-[70vh] overflow-y-auto">
            <table className="min-w-full table-auto border-collapse">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">PV NO</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">PV DATE</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">SUPPLIER</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b whitespace-normal break-words max-w-xs">COMMENTS</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">AMOUNT</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">TYPE</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">DOCS</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">JVTYPE</th>
                  <th className="px-4 py-3 text-sm text-center font-medium text-gray-700 border-b">Action</th>
                </tr>
              </thead>
              <tbody>
                {payments.length > 0 ? (
                  payments.map((payment, index) => (
                    <tr key={index} className="hover:bg-blue-50 transition-colors">
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={payment.PV_NO}>{payment.PV_NO?.trim()}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={payment.RV_DATE}>{dayjs(payment.RV_DATE).format("DD/MM/YYYY")}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={payment.SUPPLIER || payment.CUSTOMER}>{payment.SUPPLIER || payment.CUSTOMER}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600 whitespace-normal break-words max-w-xs" title={payment.COMMENTS}>{payment.COMMENTS}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={payment.AMOUNT}>{Number(payment.AMOUNT || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={payment.TYPE}>{payment.TYPE || "-"}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={payment.DOCS}>{payment.DOCS}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={payment.JVTYPE}>{payment.JVTYPE}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600">
                        <div className="flex justify-center items-center gap-2">
                          <button
                            onClick={() => onEdit(payment)}
                            className="px-2 py-1 rounded text-white bg-blue-600 flex items-center gap-1 text-sm"
                          >
                            <FaEdit className="text-white" size={12} /> Edit
                          </button>
                          <button
                            onClick={() => onPrint(payment)}
                            className="px-2 py-1 rounded text-white bg-yellow-600 flex items-center gap-1 text-sm"
                          >
                            <FaPrint className="text-white" size={12} /> Print
                          </button>
                          <button
                            className="px-2 py-1 rounded text-white bg-green-600 hover:bg-green-700 flex items-center gap-1 text-sm"
                          >
                            <FaMoneyCheckAlt size={12} /> Cheque
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-4 text-gray-500">No payments found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-gray-600">
              Showing page {currentPage} ({payments.length} payments loaded)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-lg ${currentPage === 1 ? "bg-gray-200 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-lg ${currentPage === totalPages ? "bg-gray-200 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentView;